import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  Box,
  Button,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useTranslation } from 'react-i18next';
import SearchBar from 'src/components/SearchBar';
import DownloadButton from 'src/components/DownloadButton';
import Filter from 'src/components/Filter/Filter';
import FILTER_TYPES from 'src/utils/constants/filter';
import {
  GlobalInvoiceTab,
  TransactionsCategory
} from 'src/utils/constants/transactions';
import { ClientLegalStatus } from 'src/utils/constants/clients';
import useStyles from './styles';

const dateRangeFilters = [
  {
    title: 'TRANSACTION.DATE',
    controlGroup: FILTER_TYPES.DATE_IN_RANGE,
    items: [
      {
        label: 'COMMON.START_DATE',
        value: 'afterDate',
        view: ['year', 'month', 'day']
      },
      {
        label: 'COMMON.END_DATE',
        value: 'beforeDate',
        view: ['year', 'month', 'day']
      }
    ]
  }
];

const conceptOptions = Object.values(TransactionsCategory).filter(
  (category) =>
    category !== TransactionsCategory.INTEREST &&
    category !== TransactionsCategory.DISCOUNT
);

const GlobalInvoicesToolbar = ({
  search,
  onSearchChange,
  concept,
  onConceptChange,
  legalStatus,
  onLegalStatusChange,
  dateRange,
  onDateRangeChange,
  tab,
  onTabChange,
  onClear,
  onDownload
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleTabChange = (_, value) => {
    if (value === null) return;
    onTabChange(value);
  };

  return (
    <Box className={classes.toolbar}>
      <Box className={classes.toolbarLeft}>
        <SearchBar
          searchValue={search}
          handleSearch={(event) => onSearchChange(event.target.value)}
          placeholder={t('TRANSACTION.SEARCH_GLOBAL_PLACEHOLDER')}
          sx={{ ml: 0, width: 320 }}
        />
        <Select
          className={classes.select}
          value={concept}
          onChange={(event) => onConceptChange(event.target.value)}
          displayEmpty
        >
          <MenuItem value="">{t('TRANSACTION.ALL_CONCEPTS')}</MenuItem>
          {conceptOptions.map((category) => (
            <MenuItem key={category} value={category}>
              {t(`TRANSACTION.CATEGORY.${category}`)}
            </MenuItem>
          ))}
        </Select>
        <Select
          className={classes.select}
          value={legalStatus}
          onChange={(event) => onLegalStatusChange(event.target.value)}
          displayEmpty
        >
          <MenuItem value="">{t('TRANSACTION.LEGAL_STATUS_FILTER_ALL')}</MenuItem>
          {Object.values(ClientLegalStatus).map((status) => (
            <MenuItem key={status} value={status}>
              {t(`CLIENTS_VIEW.CLIENT_DETAIL.LEGAL_STATUS_${status}`)}
            </MenuItem>
          ))}
        </Select>
        <ToggleButtonGroup
          className={classes.statusFilters}
          value={tab}
          exclusive
          onChange={handleTabChange}
        >
          <ToggleButton
            className={classes.statusChip}
            value={GlobalInvoiceTab.ALL}
          >
            {t('TRANSACTION.STATUS_FILTER_ALL')}
          </ToggleButton>
          <ToggleButton
            className={classes.statusChip}
            value={GlobalInvoiceTab.PENDING}
          >
            {t('TRANSACTION.STATUS_FILTER_PENDING')}
          </ToggleButton>
          <ToggleButton
            className={classes.statusChip}
            value={GlobalInvoiceTab.OVERDUE}
          >
            {t('TRANSACTION.STATUS_FILTER_OVERDUE')}
          </ToggleButton>
          <ToggleButton
            className={classes.statusChip}
            value={GlobalInvoiceTab.PAID}
          >
            {t('TRANSACTION.STATUS_FILTER_PAID')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box className={classes.toolbarRight}>
        <Button
          className={clsx(classes.buttons, classes.outlined)}
          color="primary"
          startIcon={<FilterListIcon className={classes.icon} />}
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          {t('COMMON.FILTER')}
        </Button>
        <Button
          className={clsx(classes.buttons, classes.outlined)}
          color="primary"
          onClick={onClear}
        >
          {t('COMMON.CLEAR_FILTERS')}
        </Button>
        <DownloadButton onDownload={onDownload} title="COMMON.DOWNLOAD_EXCEL" />
        <Filter
          anchorEl={anchorEl}
          filterValues={dateRange}
          handleClose={() => setAnchorEl(null)}
          handleChange={onDateRangeChange}
          filters={dateRangeFilters}
          allowManualInput
        />
      </Box>
    </Box>
  );
};

GlobalInvoicesToolbar.propTypes = {
  search: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  concept: PropTypes.string.isRequired,
  onConceptChange: PropTypes.func.isRequired,
  legalStatus: PropTypes.string.isRequired,
  onLegalStatusChange: PropTypes.func.isRequired,
  dateRange: PropTypes.object.isRequired,
  onDateRangeChange: PropTypes.func.isRequired,
  tab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired
};

export default GlobalInvoicesToolbar;
